// noinspection DuplicatedCode
import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {combineLatest, fromEvent} from 'rxjs';
import {TableElement, TableExportUtil, UnsubscribeOnDestroyAdapter} from '@shared';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRippleModule} from '@angular/material/core';
import {FeatherIconsComponent} from '@shared/components/feather-icons/feather-icons.component';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatTableModule} from '@angular/material/table';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BreadcrumbComponent} from '@shared/components/breadcrumb/breadcrumb.component';
import {BusinessGroupService} from '@core/service/business-group.service';
import {DoctorService} from '@core/http/table-data-services/Doctor.service';
import {Doctor} from '@core/models/Doctor';
import {Router} from '@angular/router';
import {DoctorDataSource} from '@core/data-source/DoctorDataSource';
import {DoDeleteComponent} from '@shared/components/do-delete/do-delete.component';
import {FormatMobilePipe} from '@core/pipes/format-mobile.pipe';
import {UserRole} from '@core/models/Enums/UserRole';
import {BranchGroupService} from '@core/service/branch-group.service';
import {TopWidgetsComponent} from '@shared/components/top-widgets/top-widgets.component';
import {HttpMesageHandlerService} from '@core/service/http-mesage-handler';
import {AuthManagementService} from '@core/service/auth-management.service';
import {NgIf} from "@angular/common";
import {GeneralFunctionsService} from "@core/service/generalFunctions.service";

@Component({
  selector: 'app-doctor-list',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    FeatherIconsComponent,
    MatRippleModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    FormatMobilePipe,
    TopWidgetsComponent,
    NgIf
  ],
  templateUrl: './doctor-list.component.html',
  styleUrl: './doctor-list.component.scss',
})
export class DoctorListComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit {

  displayedColumns = ['nombre', 'especialidad', 'cedula', 'email', 'asentamiento', 'acciones'];

  businessGroupId: string | null = null;  // GEMP
  idSucursal: string | null = null;       // sucursal efectiva a consultar
  branchGroupId: string | null = null;    // sucursal seleccionada (solo supervisor)

  canPickBranch = false;                  // para mostrar/ocultar selector de sucursal

  data?: DoctorService;
  dataSource!: DoctorDataSource;

  index?: number;
  idMedico?: string;
  doctor?: Doctor;
  isAdmin = false;

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild('filter', {static: true}) filter?: ElementRef;

  constructor(
    public httpClient: HttpClient,
    public dialog: MatDialog,
    private router: Router,
    private businessGroupService: BusinessGroupService,
    private doctorService: DoctorService,
    private branchGroupService: BranchGroupService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService,
    private gfs: GeneralFunctionsService,
  ) { super(); }

  ngOnInit(): void {
    const user = this.authManagement.userData();
    const role = user.TipoUsuario;

    this.canPickBranch = role === UserRole.Supervisor_Sucursales;
    this.isAdmin = role === this.authManagement.isUserAdmin();

    let lastKey = '';

    this.subs.sink = combineLatest([
      this.businessGroupService.businessGroupId$,
      this.branchGroupService.branchGroupId$
    ]).subscribe(([gempId, branchId]) => {
      this.businessGroupId = gempId ?? null;
      this.branchGroupId = branchId ?? null;

      let nextIdSucursal: string | null = null;

      if (role === UserRole.Responsable_Farmacia) {
        nextIdSucursal = (user.IdSucursal && String(user.IdSucursal).trim()) || null;
      } else if (role === UserRole.Supervisor_Sucursales) {
        nextIdSucursal = (this.branchGroupId && this.branchGroupId.trim()) || null;
      } else {
        nextIdSucursal = (this.branchGroupId && this.branchGroupId.trim()) || null;
      }

      this.idSucursal = nextIdSucursal;

      const currentKey = `${this.businessGroupId ?? ''}|${this.idSucursal ?? ''}`;

      if (!this.businessGroupId || currentKey === lastKey) return;

      lastKey = currentKey;
      this.loadData();
    });

    this.subs.sink = fromEvent(this.filter?.nativeElement, 'keyup').subscribe(() => {
      if (!this.dataSource) return;
      this.dataSource.filter = this.filter?.nativeElement.value;
    });
  }

  refresh() { this.loadData(); }

  addNew() { this.router.navigate(['doctor/create-edit']).then(); }

  editCall(row: Doctor) {
    const idUsuario = btoa(row.idMedico);
    this.router.navigate(['doctor/create-edit/' + idUsuario]).then();
  }

  deleteItem(row: Doctor) {
    this.idMedico = row.idMedico;

    const dialogRef = this.dialog.open(DoDeleteComponent, {
      data: {
        toDelete: 'Medico',
        id: this.idMedico,
        name: row.nombres,
        actionService: (id: string) => this.doctorService.deleteMedic(id),
      }
    });
    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.data?.dataChange.value.findIndex(
          (x): boolean => x.idMedico === this.idMedico
        );
        if (foundIndex != null && this.data) {
          this.data.dataChange.value.splice(foundIndex, 1);
          this.refreshTable();
        }
      }
    });
  }

  goToProfile(row: Doctor) {
    const idCodificado = btoa(row.idMedico);
    const sucursalCodificada = btoa(row.idSucursal!);
    this.router.navigate(['doctor/profile/' + idCodificado + '/' + sucursalCodificada]).then();
  }

  public loadData() {
    this.data = new DoctorService(this.httpClient, this.httpMsgHandler, this.gfs);

    this.dataSource = new DoctorDataSource(
      this.businessGroupId,
      this.data,
      this.paginator,
      this.sort,
      this.idSucursal
    );
  }

  exportExcel() {
    const exportData: Partial<TableElement>[] =
      this.dataSource.filteredData.map((x) => ({
        Nombre: x.nombres,
        Especialidad: x.especialidad,
        Cedula: x.cedulaGeneral,
        Telefono: x.movil,
        Email: x.email,
        Asentamiento: x.asentamiento
      }));
    TableExportUtil.exportToExcel(exportData, 'excel');
  }

  private refreshTable() {
    this.paginator._changePageSize(this.paginator.pageSize);
  }
}
